import { orderBy } from 'lodash'

import { CreditOffer, DepositsOffer, LifeInsuranceOffer, LoanOffer, Offer } from 'embed/constants/models'
import {
  CreditSortOptionUI,
  DepositsSortOption,
  LifeInsuranceSortOption,
  LoanSortOption,
  Product,
  SortDirection,
  SortOption,
  TermUnit,
} from 'even-ts-static'
// Helper function to use single function and just pass the product
export const sortOffers = (
  product: Product,
  offers: Offer[],
  sort: SortOption,
  direction: SortDirection,
  loanAmount?: number,
): Offer[] => {
  let sortedOffers
  switch (product) {
    case Product.LifeInsurance:
      sortedOffers = sortLifeInsuranceOffers(offers as LifeInsuranceOffer[], sort, direction)
      break
    case Product.Loan:
      sortedOffers = sortLoanOffers(offers as LoanOffer[], sort, direction, loanAmount)
      break
    case Product.Deposits:
      sortedOffers = sortDepositsOffers(offers as DepositsOffer[], sort, direction)
      break
    case Product.CreditCard:
      sortedOffers = sortCreditCardOffers(offers as CreditOffer[], sort, direction)
      break
    default:
      sortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  }

  return sortedOffers
}

export const sortLoanOffers = (
  offers: LoanOffer[],
  sort: SortOption,
  direction: SortDirection,
  loanAmount?: number,
  useConversionProbability = false,
): Offer[] => {
  const preSortedOfferSortOption = useConversionProbability ? 'conversionProbability' : 'recommendationScore'
  const presortedOffers = orderBy(offers, [preSortedOfferSortOption], [SortDirection.Desc])

  let sortedOffers
  switch (sort) {
    case LoanSortOption.Lender:
      const loanSortLender = orderBy(
        presortedOffers,
        [(offer: LoanOffer) => offer.originator?.name?.toLowerCase()],
        [direction],
      )
      sortedOffers = loanSortLender
      break
    case LoanSortOption.Term: {
      const loanSortTerm = orderBy(
        presortedOffers,
        [
          (offer: LoanOffer) => {
            const termLength = offer.termLength?.valueOf() || 0
            switch (offer.termUnit) {
              case TermUnit.Days:
                return termLength / 30
              case TermUnit.Years:
                return termLength * 12
              default:
                return termLength
            }
          },
        ],
        [direction],
      )
      sortedOffers = loanSortTerm
      break
    }
    case LoanSortOption.Apr: {
      const loanSortApr = orderBy(presortedOffers, ['meanApr'], [direction])
      sortedOffers = loanSortApr
      break
    }
    case LoanSortOption.Payment:
      const loanSortPayment = orderBy(presortedOffers, ['meanMonthlyPayment'], [direction])
      sortedOffers = loanSortPayment
      break
    case LoanSortOption.Amount:
      sortedOffers = orderBy(presortedOffers, ['maxAmount'], [direction])
      break
    case LoanSortOption.Recommended:
      sortedOffers = orderBy(presortedOffers, [preSortedOfferSortOption], [direction])
      break
    case LoanSortOption.ConversionProbability:
      sortedOffers = orderBy(presortedOffers, ['conversionProbability'], [direction])
      break
    case LoanSortOption.ClosestAmount:
      const amount = loanAmount || 0
      sortedOffers = presortedOffers.sort((a: LoanOffer, b: LoanOffer) => {
        const aValue = Math.abs(amount - (a?.maxAmount ?? 0))
        const bValue = Math.abs(amount - (b?.maxAmount ?? 0))

        if (aValue === bValue) {
          return 0
        } else if (direction === 'asc') {
          return aValue < bValue ? -1 : 1
        } else {
          return aValue > bValue ? -1 : 1
        }
      })
      break
    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const sortDepositsOffers = (offers: DepositsOffer[], sort: SortOption, direction: SortDirection): Offer[] => {
  const presortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  let sortedOffers
  switch (sort) {
    // Deposits Sort Options
    case DepositsSortOption.APY:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: DepositsOffer) => offer.details?.annualPercentYield],
        [direction],
      )
      break
    case DepositsSortOption.Minimum:
      sortedOffers = orderBy(presortedOffers, [(offer: DepositsOffer) => offer.details?.minimumDeposit], [direction])
      break
    case DepositsSortOption.Partner:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: DepositsOffer) => offer.partner?.name?.toLocaleLowerCase()],
        [direction],
      )
      break
    case DepositsSortOption.Recommended:
      sortedOffers = orderBy(presortedOffers, ['recommendationScore'], [direction])
      break

    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const sortCreditCardOffers = (offers: CreditOffer[], sort: SortOption, direction: SortDirection): Offer[] => {
  const presortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  let sortedOffers

  switch (sort) {
    // Credit Card Sorts
    case CreditSortOptionUI.Recommended:
      sortedOffers = orderBy(
        presortedOffers,
        ['recommendationScore', 'details.minPurchaseApr'],
        [direction, SortDirection.Asc],
      )

      break
    case CreditSortOptionUI.Rewards:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: CreditOffer) => offer.details.earningProgram?.personalization?.totalValue ?? 0],
        [direction],
      )
      break
    case CreditSortOptionUI.Name:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: CreditOffer) => offer.details.cardName?.toLowerCase()],
        [direction],
      )
      break
    case CreditSortOptionUI.VariableApr:
      sortedOffers = orderBy(presortedOffers, [(offer: CreditOffer) => offer.details.minPurchaseApr], [direction])
      break
    case CreditSortOptionUI.AnnualFee:
      sortedOffers = orderBy(
        presortedOffers,
        [(offer: CreditOffer) => offer.details.maxAnnualFee || offer.details.minAnnualFee],
        [direction],
      )
      break
    case CreditSortOptionUI.IntroApr:
      sortedOffers = orderBy(presortedOffers, [(offer: CreditOffer) => offer.details?.minPurchaseIntroApr], [direction])
      break

    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const sortLifeInsuranceOffers = (
  offers: LifeInsuranceOffer[],
  sort: SortOption,
  direction: SortDirection,
): Offer[] => {
  const presortedOffers = orderBy(offers, ['recommendationScore'], [SortDirection.Desc])
  let sortedOffers
  switch (sort) {
    // Life Insurance Sorts
    case LifeInsuranceSortOption.Recommended:
      const recommendedOffers = presortedOffers.filter((offer: LifeInsuranceOffer) => offer.details?.recommended)
      const nonrecommendedOffers = presortedOffers.filter((offer: LifeInsuranceOffer) => !offer.details?.recommended)

      const sortedRecommendedOffers = orderBy(recommendedOffers, ['details.monthlyPremiumAmount'], [SortDirection.Asc])
      const sortedNonrecommendedOffers = orderBy(nonrecommendedOffers, ['recommendationScore'], [SortDirection.Desc])
      sortedOffers = [...sortedRecommendedOffers, ...sortedNonrecommendedOffers]

      if (direction === SortDirection.Asc) {
        sortedOffers = sortedOffers.reverse()
      }

      break
    case LifeInsuranceSortOption.PolicyAmount:
      sortedOffers = orderBy(
        presortedOffers,
        ['details.policyAmount', 'details.monthlyPremiumAmount'],
        [direction, SortDirection.Asc],
      )
      break
    case LifeInsuranceSortOption.TermLength:
      sortedOffers = orderBy(
        presortedOffers,
        ['details.termLength', 'details.monthlyPremiumAmount'],
        [direction, SortDirection.Asc],
      )
      break
    case LifeInsuranceSortOption.MonthlyPremiumAmount:
      sortedOffers = orderBy(
        presortedOffers,
        ['details.monthlyPremiumAmount', 'recommendationScore'],
        [direction, SortDirection.Desc],
      )
      break
    default:
      sortedOffers = presortedOffers
  }

  return sortedOffers
}

export const groupOffersByLenderId = (offers: Offer[] = [], key: string) => {
  const [offerKey, offerSubkey] = key.split('.')
  return offers.reduce((groupedOffers, offer) => {
    const lenderIdKey = offerSubkey ? offer[offerKey][offerSubkey] : offer[offerKey]
    groupedOffers[lenderIdKey] = [...(groupedOffers[lenderIdKey] ?? []), offer]
    return groupedOffers
  }, {})
}
